// Pregunta 4: ¿Qué características estás buscando de la vivienda?

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Home, Bath, Maximize2, TreePine, Wrench, Building, Car } from 'lucide-react';
import { OUTDOOR_OPTIONS, REFORM_OPTIONS, FEATURE_OPTIONS } from '../../../config/buyerProfileOptions';

export const PropertyFeaturesQuestion = ({
  minBedrooms,
  setMinBedrooms,
  minBathrooms,
  setMinBathrooms,
  desiredSqm,
  setDesiredSqm,
  outdoorSpace,
  setOutdoorSpace,
  reformLevel,
  setReformLevel,
  elevator,
  setElevator,
  parking,
  setParking
}) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-white mb-1">
          {t('questions.features.question')}
        </h3>
        <p className="text-xs text-slate-400">{t('questions.features.optional')}</p>
      </div>

      {/* Habitaciones y Baños */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-300 mb-2">
            <Home className="w-4 h-4 text-amber-400" />
            {t('questions.features.minBedrooms')}
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={minBedrooms || ''}
            onChange={(e) => setMinBedrooms(e.target.value ? parseInt(e.target.value) : null)}
            placeholder={t('questions.features.egBedrooms')}
            className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-300 mb-2">
            <Bath className="w-4 h-4 text-amber-400" />
            {t('questions.features.minBathrooms')}
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={minBathrooms || ''}
            onChange={(e) => setMinBathrooms(e.target.value ? parseInt(e.target.value) : null)}
            placeholder={t('questions.features.egBathrooms')}
            className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
          />
        </div>
      </div>

      {/* M² */}
      <div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-300 mb-2">
          <Maximize2 className="w-4 h-4 text-amber-400" />
          {t('questions.features.desiredSqm')}
        </label>
        <input
          type="number"
          min="0"
          max="10000"
          value={desiredSqm || ''}
          onChange={(e) => setDesiredSqm(e.target.value ? parseInt(e.target.value) : null)}
          placeholder={t('questions.features.egSqm')}
          className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
        />
      </div>

      {/* Zona exterior (terraza/balcón) */}
      <div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-300 mb-2">
          <TreePine className="w-4 h-4 text-amber-400" />
          {t('questions.features.outdoorSpace')}
        </label>
        <div className="grid grid-cols-3 gap-2">
          {OUTDOOR_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                outdoorSpace === option.value
                  ? 'bg-amber-500/10 border-amber-500 text-white'
                  : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="outdoor_space"
                value={option.value}
                checked={outdoorSpace === option.value}
                onChange={(e) => setOutdoorSpace(e.target.value)}
                className="hidden"
              />
              <span>{t(option.labelKey)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Nivel de reforma */}
      <div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-300 mb-2">
          <Wrench className="w-4 h-4 text-amber-400" />
          {t('questions.features.reformLevel')}
        </label>
        <div className="grid grid-cols-2 gap-2">
          {REFORM_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                reformLevel === option.value
                  ? 'bg-amber-500/10 border-amber-500 text-white'
                  : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="reform_level"
                value={option.value}
                checked={reformLevel === option.value}
                onChange={(e) => setReformLevel(e.target.value)}
                className="hidden"
              />
              <span>{t(option.labelKey)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Ascensor */}
      <div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-300 mb-2">
          <Building className="w-4 h-4 text-amber-400" />
          {t('questions.features.elevator')}
        </label>
        <div className="grid grid-cols-3 gap-2">
          {FEATURE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                elevator === option.value
                  ? 'bg-amber-500/10 border-amber-500 text-white'
                  : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="elevator"
                value={option.value}
                checked={elevator === option.value}
                onChange={(e) => setElevator(e.target.value)}
                className="hidden"
              />
              <span>{t(option.labelKey)}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Parking */}
      <div>
        <label className="flex items-center gap-2 text-xs font-medium text-slate-300 mb-2">
          <Car className="w-4 h-4 text-amber-400" />
          {t('questions.features.parking')}
        </label>
        <div className="grid grid-cols-3 gap-2">
          {FEATURE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-center justify-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                parking === option.value
                  ? 'bg-amber-500/10 border-amber-500 text-white'
                  : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="parking"
                value={option.value}
                checked={parking === option.value}
                onChange={(e) => setParking(e.target.value)}
                className="hidden"
              />
              <span>{t(option.labelKey)}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PropertyFeaturesQuestion;
